import { toast } from "@overmap-ai/blocks"
import { type Asset, type AssetTypeStatus, type Payload, type Stored, ERROR_MSG_UNEXPECTED } from "@overmap-ai/common"
import { selectAssetTypeById, selectAssetTypeStatusById } from "@overmap-ai/core"
import type { JSONContent } from "@tiptap/react"
import { useCallback, useMemo } from "react"

import { sdk } from "../sdk"
import { useAppSelector } from "../store"
import {
	selectAssetTypeIdentifierValuesByAsset,
	selectAttachmentsByAssetTypeFieldValues,
	selectIssueAssociationsByAsset,
	selectLatestAssetTypeFieldValuesByAsset,
} from "../store/selectors"
import type { AssetAndIdentifiers, AssetData } from "../typings"
import { useConfirmDeleteDialog } from "./utils"

export const useAssetData = (assets: Stored<Asset>[]) => {
	const issueAssociationsByAsset = useAppSelector(selectIssueAssociationsByAsset)
	const assetTypeFieldValuesByAsset = useAppSelector(selectLatestAssetTypeFieldValuesByAsset)
	const assetTypeIdentifierValuesByAsset = useAppSelector(selectAssetTypeIdentifierValuesByAsset)
	const attachmentsByAssetTypeFieldValues = useAppSelector(selectAttachmentsByAssetTypeFieldValues)

	return useMemo(() => {
		const ret: AssetData[] = []

		for (const asset of assets) {
			const assetTypeIdentifierValues = assetTypeIdentifierValuesByAsset[asset.uuid] ?? {}
			const assetTypeFieldValues = assetTypeFieldValuesByAsset[asset.uuid]
			const assetTypeFieldValuesAttachments =
				attachmentsByAssetTypeFieldValues[assetTypeFieldValues?.uuid ?? ""] ?? []
			const issueAssociations = issueAssociationsByAsset[asset.uuid] ?? []

			ret.push({
				asset,
				assetTypeIdentifierValues,
				assetTypeFieldValues,
				assetTypeFieldValuesAttachments,
				issueAssociations,
			})
		}

		return ret
	}, [
		assetTypeFieldValuesByAsset,
		attachmentsByAssetTypeFieldValues,
		assetTypeIdentifierValuesByAsset,
		assets,
		issueAssociationsByAsset,
	])
}

export const useAsset = (asset: Stored<Asset>) => {
	const assetType = useAppSelector(selectAssetTypeById(asset.asset_type))!
	const assetTypeStatus = useAppSelector(selectAssetTypeStatusById(asset.status))!

	const updateAsset = useCallback(
		(assetPatch: Partial<Payload<Asset>>) => {
			return sdk.assets.update({
				uuid: asset.uuid,
				...assetPatch,
			})
		},
		[asset.uuid],
	)

	const handleLabelChange = useCallback(
		(label: string) => {
			return updateAsset({ label })
		},
		[updateAsset],
	)

	const handleDescriptionChange = useCallback(
		(content: JSONContent | undefined) => {
			return updateAsset({ description: content ? JSON.stringify(content) : undefined })
		},
		[updateAsset],
	)

	const handleStatusChange = useCallback(
		(status: Stored<AssetTypeStatus>) => {
			return updateAsset({ status: status.uuid })
		},
		[updateAsset],
	)

	return {
		assetType,
		assetTypeStatus,
		updateAsset,
		handleLabelChange,
		handleDescriptionChange,
		handleStatusChange,
	}
}

export const useDeleteAsset = () => {
	const openConfirmDeleteDialog = useConfirmDeleteDialog()

	return useCallback(
		(assetId: Asset["uuid"], onConfirm?: () => void | Promise<void>) => {
			openConfirmDeleteDialog({
				title: "Delete asset",
				description: "Are you sure you want to delete this asset? This cannot be undone.",
				onAction: async () => {
					if (onConfirm) await onConfirm()

					try {
						return await sdk.assets.delete(assetId)
					} catch (e: unknown) {
						toast.error({
							title: `Failed to delete the asset.`,
							description: e instanceof Error ? e.message : ERROR_MSG_UNEXPECTED,
						})
					}
				},
			})
		},
		[openConfirmDeleteDialog],
	)
}

export const useAssetsWithIdentifiers = (assets: Stored<Asset>[]): AssetAndIdentifiers[] => {
	const assetTypeIdentifierValuesByAsset = useAppSelector(selectAssetTypeIdentifierValuesByAsset)

	return useMemo(() => {
		return assets.map((asset) => ({
			asset,
			identifierValues: Object.values(assetTypeIdentifierValuesByAsset[asset.uuid] ?? {}),
		}))
	}, [assetTypeIdentifierValuesByAsset, assets])
}
